import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Flame, Save, FileText } from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import AdminDocumentsList from "../components/AdminDocumentsList";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function AdminLeadDetailPage() {
    const { leadId } = useParams();
    const navigate = useNavigate();
    const [lead, setLead] = useState(null);
    const [status, setStatus] = useState("new");
    const [notes, setNotes] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/admin/leads/${leadId}`);
                setLead(data);
                setStatus(data.status || "new");
                setNotes(data.advisor_notes || "");
            } catch {
                toast.error("Lead nicht gefunden");
                navigate("/admin");
            } finally {
                setLoading(false);
            }
        })();
    }, [leadId, navigate]);

    const save = async () => {
        try {
            const { data } = await api.patch(`/admin/leads/${leadId}`, {
                status,
                advisor_notes: notes,
            });
            setLead(data);
            toast.success("Lead aktualisiert");
        } catch {
            toast.error("Speichern fehlgeschlagen");
        }
    };

    if (loading || !lead) {
        return <div className="py-20 text-center text-sm text-ink-500">Lade…</div>;
    }

    const fd = lead.form_data || {};

    return (
        <div className="min-h-[80vh] bg-brand-50/10 py-10">
            <div className="container max-w-4xl">
                <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-ink-500">
                    <ArrowLeft className="h-4 w-4" /> Zurück zur Lead-Liste
                </Link>

                <div className="mt-4 flex flex-wrap items-center gap-3">
                    <h1 className="font-display text-3xl font-bold">
                        {fd.first_name} {fd.last_name}
                    </h1>
                    <span className="rounded-full bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand-700">
                        {lead.package_interest}
                    </span>
                    {lead.high_intent && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700">
                            <Flame className="h-3.5 w-3.5" /> High Intent
                        </span>
                    )}
                </div>

                <div className="mt-6 grid gap-6 lg:grid-cols-3">
                    <div className="rounded-3xl border border-ink-300/30 bg-white p-6 lg:col-span-2">
                        <h2 className="font-display text-lg font-semibold">Angaben</h2>
                        <dl className="mt-4 grid gap-3 sm:grid-cols-2">
                            {[
                                ["E-Mail", fd.email],
                                ["Telefon", fd.phone],
                                ["Rolle", fd.relation],
                                ["Pflegegrad", fd.care_grade],
                                ["Heim-Status", fd.nursing_home_status],
                                ["Einkommen", fd.income_band],
                                ["Score", lead.lead_score],
                                ["Erstellt", new Date(lead.created_at).toLocaleString("de-DE")],
                            ].map(([k, v]) => (
                                <div key={k} className="rounded-xl bg-brand-50/40 px-4 py-3">
                                    <div className="text-[11px] uppercase tracking-wider text-ink-500">{k}</div>
                                    <div className="mt-0.5 text-sm font-medium text-ink-900">{v || "—"}</div>
                                </div>
                            ))}
                        </dl>
                        {fd.message && (
                            <>
                                <h3 className="mt-6 font-display text-base font-semibold">Nachricht</h3>
                                <p className="mt-2 whitespace-pre-wrap rounded-xl bg-brand-50/40 p-4 text-sm text-ink-700">
                                    {fd.message}
                                </p>
                            </>
                        )}

                        {lead.case && (
                            <>
                                <h3 className="mt-6 font-display text-base font-semibold">Zugehöriger Fall</h3>
                                <div className="mt-2 rounded-xl bg-brand-50/40 p-4 text-sm text-ink-700">
                                    Risiko: {lead.case.risk_level} · Paket-Empfehlung: {lead.case.package_recommendation}
                                    <br />
                                    Region: {lead.case.answers?.region}
                                </div>
                            </>
                        )}
                    </div>

                    <div className="space-y-4">
                        <div className="rounded-3xl border border-ink-300/30 bg-white p-6">
                            <h3 className="font-display text-base font-semibold">Status</h3>
                            <Select value={status} onValueChange={setStatus}>
                                <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="admin-lead-status"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="new">Neu</SelectItem>
                                    <SelectItem value="contacted">Kontaktiert</SelectItem>
                                    <SelectItem value="qualified">Qualifiziert</SelectItem>
                                    <SelectItem value="won">Abgeschlossen</SelectItem>
                                    <SelectItem value="lost">Verloren</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="rounded-3xl border border-ink-300/30 bg-white p-6">
                            <h3 className="font-display text-base font-semibold">Notizen</h3>
                            <Textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                className="mt-2 min-h-[140px] rounded-xl"
                                placeholder="Interne Notizen zum Lead …"
                                data-testid="admin-lead-notes"
                            />
                        </div>

                        <Button onClick={save} className="h-12 w-full rounded-full bg-brand-500 hover:bg-brand-600" data-testid="admin-lead-save">
                            <Save className="mr-2 h-4 w-4" /> Speichern
                        </Button>
                    </div>
                </div>

                {/* Nutzer-Dokumente */}
                {lead.user_id && (
                    <div className="mt-10 rounded-3xl border border-ink-300/30 bg-white p-6">
                        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand-600">
                            <FileText className="h-4 w-4" /> Hochgeladene Dokumente des Nutzers
                        </div>
                        <p className="mt-1 text-sm text-ink-500">
                            KI-Voranalyse immer sichtbar. Original nur bei Freigabe durch den Nutzer abrufbar.
                        </p>
                        <div className="mt-4">
                            <AdminDocumentsList userId={lead.user_id} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
